<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Letter } from 'vue-letter';
import { allowedCssProperties } from 'lettersanitizer';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import EmailFoldersAPI from 'dashboard/api/emailFolders';
import {
  buildResourceRewriter,
  hasRemoteResources,
} from 'dashboard/helper/emailRemoteResources';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

// Spam/Lixo eletrônico e Lixeira lidos ao vivo no servidor do cliente. É só leitura:
// nada vira contato ou conversa, nem aciona IA, automações ou notificações.
const route = useRoute();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const folder = computed(() =>
  route.params.folder === 'trash' ? 'trash' : 'junk'
);
const isJunk = computed(() => folder.value === 'junk');

const inboxes = ref([]);
const inboxId = ref(null);
const listing = ref({ items: [], total: 0, uidvalidity: null });
const page = ref(1);
const pageTokens = ref([null]);
const currentInbox = computed(() =>
  inboxes.value.find(inbox => inbox.id === inboxId.value)
);
const isGmail = computed(() => currentInbox.value?.provider === 'google');
const canMove = computed(() => currentInbox.value?.can_move !== false);
const canDeletePermanently = computed(
  () =>
    isAdmin.value &&
    !!currentInbox.value &&
    !isGmail.value &&
    currentInbox.value.can_delete_permanently === true
);
const isLoading = ref(false);
const loadError = ref('');
const selectedUids = ref([]);
const openedUid = ref(null);
const opened = ref(null);
const isOpening = ref(false);
const showRemoteImages = ref(false);
const isMoving = ref(false);
const deleteDialogRef = ref(null);
const riskyDialogRef = ref(null);
const pendingRiskyAttachment = ref(null);
let inboxRequest = 0;
let listRequest = 0;
let messageRequest = 0;

// Caixas ativas primeiro; as que precisam reconectar ficam num grupo separado
// no fim, com ícone de alerta.
const inboxOptions = computed(() => {
  const toOption = inbox => ({
    value: inbox.id,
    label: `${inbox.name} · ${inbox.email}`,
  });
  const active = inboxes.value.filter(inbox => !inbox.reauthorization_required);
  const broken = inboxes.value.filter(inbox => inbox.reauthorization_required);
  return [
    ...active.map(toOption),
    ...broken.map(inbox => ({
      ...toOption(inbox),
      icon: 'i-hugeicons-alert-02',
      iconClass: 'text-n-amber-11',
      iconLabel: t('EMAIL_FOLDERS.NEEDS_RECONNECT'),
      groupLabel: active.length ? t('EMAIL_FOLDERS.NEEDS_RECONNECT_GROUP') : '',
    })),
  ];
});
// Caixa cuja conexão Google/Microsoft expirou: a tela leva direto às configurações dela.
const reconnectInboxId = ref(null);
const perPage = 30;
const totalPages = computed(() =>
  Math.max(1, Math.ceil((listing.value.total || 0) / perPage))
);
const hasNextPage = computed(() =>
  isGmail.value
    ? !!listing.value.next_page_token
    : page.value < totalPages.value
);
const showPagination = computed(() => page.value > 1 || hasNextPage.value);
const allSelected = computed({
  get: () =>
    listing.value.items.length > 0 &&
    selectedUids.value.length === listing.value.items.length,
  set: value => {
    selectedUids.value = value ? listing.value.items.map(item => item.uid) : [];
  },
});

const formatDate = value =>
  value
    ? new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : '';
const formatSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
const senderLabel = from =>
  from?.name || from?.email || t('EMAIL_FOLDERS.UNKNOWN_SENDER');
const errorMessage = error =>
  error?.response?.data?.error || t('EMAIL_FOLDERS.GENERIC_ERROR');

const resetMessage = () => {
  messageRequest += 1;
  isOpening.value = false;
  openedUid.value = null;
  opened.value = null;
  showRemoteImages.value = false;
};

const resetPagination = () => {
  page.value = 1;
  pageTokens.value = [null];
};

const loadList = async () => {
  if (!inboxId.value) return;
  listRequest += 1;
  const request = listRequest;
  isLoading.value = true;
  loadError.value = '';
  reconnectInboxId.value = null;
  try {
    const params = {
      inbox_id: inboxId.value,
      folder: folder.value,
      page: page.value,
    };
    if (isGmail.value) params.page_token = pageTokens.value[page.value - 1];
    const { data } = await EmailFoldersAPI.list(params);
    if (request !== listRequest) return;
    listing.value = data;
    selectedUids.value = [];
  } catch (error) {
    if (request !== listRequest) return;
    listing.value = { items: [], total: 0, uidvalidity: null };
    loadError.value = errorMessage(error);
    reconnectInboxId.value =
      error?.response?.data?.code === 'reauthorization_required'
        ? error.response.data.inbox_id
        : null;
  } finally {
    if (request === listRequest) isLoading.value = false;
  }
};

const refreshList = () => {
  if (isGmail.value) resetPagination();
  resetMessage();
  loadList();
};

const loadInboxes = async () => {
  inboxRequest += 1;
  const request = inboxRequest;
  try {
    const { data } = await EmailFoldersAPI.inboxes();
    if (request !== inboxRequest) return;
    inboxes.value = data.inboxes || [];
    if (!inboxes.value.some(inbox => inbox.id === inboxId.value)) {
      inboxId.value =
        (
          inboxes.value.find(inbox => !inbox.reauthorization_required) ||
          inboxes.value[0]
        )?.id || null;
    }
  } catch (error) {
    if (request === inboxRequest) loadError.value = errorMessage(error);
  }
};

const openMessage = async item => {
  messageRequest += 1;
  const request = messageRequest;
  openedUid.value = item.uid;
  opened.value = null;
  showRemoteImages.value = false;
  isOpening.value = true;
  try {
    const { data } = await EmailFoldersAPI.message({
      inbox_id: inboxId.value,
      folder: folder.value,
      uid: item.uid,
      uidvalidity: listing.value.uidvalidity,
    });
    if (request === messageRequest) opened.value = data;
  } catch (error) {
    if (request === messageRequest) {
      openedUid.value = null;
      useAlert(errorMessage(error));
    }
  } finally {
    if (request === messageRequest) isOpening.value = false;
  }
};

const toggleSelected = uid => {
  selectedUids.value = selectedUids.value.includes(uid)
    ? selectedUids.value.filter(value => value !== uid)
    : [...selectedUids.value, uid];
};

const targetUids = () =>
  selectedUids.value.length ? selectedUids.value : [openedUid.value];

const runOperation = async operation => {
  if (!canMove.value) return;
  if (operation === 'delete_permanently' && !canDeletePermanently.value) return;
  const uids = targetUids().filter(Boolean);
  if (!uids.length || isMoving.value) return;
  isMoving.value = true;
  try {
    await EmailFoldersAPI.move({
      inbox_id: inboxId.value,
      operation,
      uids,
      uidvalidity: listing.value.uidvalidity,
    });
    useAlert(
      t(
        `EMAIL_FOLDERS.SUCCESS.${operation.toUpperCase()}`,
        { count: uids.length },
        uids.length
      )
    );
    deleteDialogRef.value?.close();
    resetMessage();
    if (isGmail.value) resetPagination();
    await loadList();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isMoving.value = false;
  }
};

const downloadAttachment = async attachment => {
  try {
    const { data } = await EmailFoldersAPI.attachment({
      inbox_id: inboxId.value,
      folder: folder.value,
      uid: openedUid.value,
      uidvalidity: listing.value.uidvalidity,
      index: attachment.index,
    });
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.filename;
    link.click();
    URL.revokeObjectURL(url);
  } catch {
    useAlert(t('EMAIL_FOLDERS.ATTACHMENT_ERROR'));
  }
};

const requestAttachment = attachment => {
  if (!attachment.risky) {
    downloadAttachment(attachment);
    return;
  }
  pendingRiskyAttachment.value = attachment;
  riskyDialogRef.value?.open();
};

const confirmRiskyDownload = () => {
  const attachment = pendingRiskyAttachment.value;
  riskyDialogRef.value?.close();
  if (attachment) downloadAttachment(attachment);
};

const resourceRewriter = computed(() =>
  buildResourceRewriter({ allowRemote: showRemoteImages.value })
);
const hasBlockedImages = computed(
  () => !showRemoteImages.value && hasRemoteResources(opened.value?.html)
);

const changePage = delta => {
  if (isLoading.value || (delta > 0 && !hasNextPage.value)) return;
  if (delta < 0 && page.value <= 1) return;
  if (isGmail.value && delta > 0) {
    pageTokens.value = pageTokens.value.slice(0, page.value);
    pageTokens.value.push(listing.value.next_page_token);
  }
  page.value += delta;
  resetMessage();
  loadList();
};

watch(
  () => [folder.value, inboxId.value],
  () => {
    listRequest += 1;
    listing.value = { items: [], total: 0, uidvalidity: null };
    selectedUids.value = [];
    resetPagination();
    resetMessage();
    loadList();
  }
);

watch(
  () => route.params.accountId,
  () => {
    listRequest += 1;
    inboxes.value = [];
    inboxId.value = null;
    listing.value = { items: [], total: 0, uidvalidity: null };
    selectedUids.value = [];
    loadError.value = '';
    reconnectInboxId.value = null;
    isLoading.value = false;
    resetPagination();
    resetMessage();
    loadInboxes();
  },
  { immediate: true }
);
</script>

<template>
  <section
    class="flex h-full min-h-0 w-full flex-col overflow-hidden bg-n-surface-1"
  >
    <header
      class="flex flex-wrap items-center gap-3 border-b border-n-weak px-4 py-3"
    >
      <span
        class="grid size-10 shrink-0 place-content-center rounded-lg"
        :class="
          isJunk
            ? 'bg-n-amber-3 text-n-amber-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
        aria-hidden="true"
      >
        <span
          :class="isJunk ? 'i-hugeicons-spam' : 'i-hugeicons-delete-02'"
          class="size-5"
        />
      </span>
      <div class="min-w-0 flex-1">
        <h1 class="m-0 text-lg font-semibold text-n-slate-12">
          {{
            isJunk
              ? $t('EMAIL_FOLDERS.JUNK_TITLE')
              : $t('EMAIL_FOLDERS.TRASH_TITLE')
          }}
        </h1>
        <p class="m-0 text-xs text-n-slate-11">
          {{ $t('EMAIL_FOLDERS.READ_ONLY_HINT') }}
        </p>
      </div>
      <div v-if="inboxOptions.length > 1" class="w-full sm:w-72">
        <ComboBox
          v-model="inboxId"
          :options="inboxOptions"
          :placeholder="$t('EMAIL_FOLDERS.SELECT_INBOX')"
        />
      </div>
      <Button
        :label="$t('EMAIL_FOLDERS.REFRESH')"
        icon="i-hugeicons-refresh"
        slate
        faded
        sm
        :is-loading="isLoading"
        :disabled="!inboxId"
        @click="refreshList"
      />
    </header>

    <p
      v-if="isJunk"
      class="m-0 flex items-start gap-2 border-b border-n-amber-6 bg-n-amber-2 px-4 py-2 text-xs leading-5 text-n-amber-12"
      data-test-id="email-folder-warning"
    >
      <span
        class="i-hugeicons-alert-02 mt-0.5 size-4 shrink-0"
        aria-hidden="true"
      />
      {{ $t('EMAIL_FOLDERS.JUNK_WARNING') }}
    </p>

    <p v-if="!inboxes.length && !loadError" class="m-6 text-sm text-n-slate-11">
      {{ $t('EMAIL_FOLDERS.NO_INBOX') }}
    </p>
    <p
      v-if="loadError"
      class="m-4 rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
      role="alert"
    >
      {{ loadError }}
      <router-link
        v-if="reconnectInboxId"
        class="ml-1 font-semibold underline"
        data-test-id="email-folder-reconnect"
        :to="{
          name: 'settings_inbox_show',
          params: {
            accountId: route.params.accountId,
            inboxId: reconnectInboxId,
          },
        }"
      >
        {{ $t('EMAIL_FOLDERS.OPEN_INBOX_SETTINGS') }}
      </router-link>
    </p>

    <p
      v-if="isGmail && !canMove"
      class="mx-4 rounded-lg bg-n-blue-3 p-3 text-sm text-n-blue-11"
      data-testid="gmail-read-only-notice"
    >
      {{ $t('EMAIL_FOLDERS.GMAIL_READ_ONLY') }}
    </p>

    <div
      v-if="inboxId && !loadError"
      class="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]"
    >
      <div
        class="flex min-h-0 flex-col border-n-weak lg:border-r"
        :class="{ 'hidden lg:flex': openedUid }"
      >
        <div
          class="flex flex-wrap items-center gap-2 border-b border-n-weak px-3 py-2"
        >
          <Checkbox v-model="allSelected" :disabled="!listing.items.length" />
          <span class="flex-1 text-xs text-n-slate-11">
            {{
              listing.total_estimated
                ? $t(
                    'EMAIL_FOLDERS.TOTAL_ESTIMATED',
                    { count: listing.total },
                    listing.total
                  )
                : $t(
                    'EMAIL_FOLDERS.TOTAL',
                    { count: listing.total },
                    listing.total
                  )
            }}
          </span>
          <template v-if="selectedUids.length && canMove">
            <Button
              v-if="isJunk"
              :label="$t('EMAIL_FOLDERS.ACTIONS.NOT_SPAM')"
              icon="i-hugeicons-inbox"
              xs
              faded
              :disabled="isMoving"
              @click="runOperation('not_spam')"
            />
            <Button
              v-else
              :label="$t('EMAIL_FOLDERS.ACTIONS.RESTORE')"
              icon="i-hugeicons-undo"
              xs
              faded
              :disabled="isMoving"
              @click="runOperation('restore')"
            />
            <Button
              v-if="isAdmin && isJunk"
              :label="$t('EMAIL_FOLDERS.ACTIONS.TRASH')"
              icon="i-hugeicons-delete-02"
              xs
              ruby
              faded
              :disabled="isMoving"
              @click="runOperation('trash')"
            />
            <Button
              v-if="canDeletePermanently && !isJunk"
              :label="$t('EMAIL_FOLDERS.ACTIONS.DELETE_PERMANENTLY')"
              icon="i-hugeicons-delete-02"
              xs
              ruby
              faded
              :disabled="isMoving"
              @click="deleteDialogRef?.open()"
            />
          </template>
        </div>
        <ul
          class="m-0 min-h-0 flex-1 list-none overflow-y-auto p-0"
          :aria-busy="isLoading"
        >
          <li
            v-for="item in listing.items"
            :key="item.uid"
            class="flex items-start gap-2 border-b border-n-weak px-3 py-2.5"
            :class="
              openedUid === item.uid ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-1'
            "
          >
            <Checkbox
              :model-value="selectedUids.includes(item.uid)"
              class="mt-0.5"
              @update:model-value="toggleSelected(item.uid)"
            />
            <button
              type="button"
              class="grid min-w-0 flex-1 gap-0.5 text-left"
              :data-test-id="`email-folder-item-${item.uid}`"
              @click="openMessage(item)"
            >
              <span class="flex items-center gap-2">
                <strong
                  class="min-w-0 flex-1 truncate text-sm"
                  :class="
                    item.seen
                      ? 'font-medium text-n-slate-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ senderLabel(item.from) }}
                </strong>
                <span class="shrink-0 text-xs text-n-slate-10">
                  {{ formatDate(item.date) }}
                </span>
              </span>
              <span class="truncate text-sm text-n-slate-12">
                {{ item.subject || $t('EMAIL_FOLDERS.NO_SUBJECT') }}
              </span>
              <span class="truncate text-xs text-n-slate-10">
                {{ item.from?.email }} · {{ formatSize(item.size) }}
              </span>
            </button>
          </li>
          <li
            v-if="!isLoading && !listing.items.length"
            class="px-4 py-8 text-center text-sm text-n-slate-11"
          >
            {{ $t('EMAIL_FOLDERS.EMPTY') }}
          </li>
        </ul>
        <footer
          v-if="showPagination"
          class="flex items-center justify-between gap-2 border-t border-n-weak px-3 py-2 text-xs text-n-slate-11"
        >
          <Button
            icon="i-hugeicons-arrow-left-01"
            xs
            slate
            ghost
            data-test-id="email-folder-page-previous"
            :aria-label="$t('EMAIL_FOLDERS.PREVIOUS_PAGE')"
            :disabled="page <= 1 || isLoading"
            @click="changePage(-1)"
          />
          {{
            isGmail
              ? $t('EMAIL_FOLDERS.PAGE_CURSOR', { page })
              : $t('EMAIL_FOLDERS.PAGE', { page, total: totalPages })
          }}
          <Button
            icon="i-hugeicons-arrow-right-01"
            xs
            slate
            ghost
            data-test-id="email-folder-page-next"
            :aria-label="$t('EMAIL_FOLDERS.NEXT_PAGE')"
            :disabled="!hasNextPage || isLoading"
            @click="changePage(1)"
          />
        </footer>
      </div>

      <div
        class="min-h-0 flex-col overflow-y-auto"
        :class="openedUid ? 'flex' : 'hidden lg:flex'"
      >
        <div
          v-if="!openedUid"
          class="m-auto max-w-sm px-6 text-center text-sm text-n-slate-11"
        >
          {{ $t('EMAIL_FOLDERS.SELECT_MESSAGE') }}
        </div>
        <div v-else-if="isOpening" class="m-auto text-sm text-n-slate-11">
          {{ $t('EMAIL_FOLDERS.LOADING_MESSAGE') }}
        </div>
        <article
          v-else-if="opened"
          class="grid gap-3 p-4"
          data-test-id="email-folder-preview"
        >
          <div class="flex flex-wrap items-center gap-2">
            <Button
              class="lg:hidden"
              icon="i-hugeicons-arrow-left-01"
              xs
              slate
              ghost
              :label="$t('EMAIL_FOLDERS.BACK')"
              @click="resetMessage"
            />
            <div v-if="canMove" class="ml-auto flex flex-wrap gap-2">
              <Button
                v-if="isJunk"
                :label="$t('EMAIL_FOLDERS.ACTIONS.NOT_SPAM')"
                icon="i-hugeicons-inbox"
                sm
                faded
                :disabled="isMoving"
                @click="runOperation('not_spam')"
              />
              <Button
                v-else
                :label="$t('EMAIL_FOLDERS.ACTIONS.RESTORE')"
                icon="i-hugeicons-undo"
                sm
                faded
                :disabled="isMoving"
                @click="runOperation('restore')"
              />
              <Button
                v-if="isAdmin && isJunk"
                :label="$t('EMAIL_FOLDERS.ACTIONS.TRASH')"
                icon="i-hugeicons-delete-02"
                sm
                ruby
                faded
                :disabled="isMoving"
                @click="runOperation('trash')"
              />
              <Button
                v-if="canDeletePermanently && !isJunk"
                :label="$t('EMAIL_FOLDERS.ACTIONS.DELETE_PERMANENTLY')"
                icon="i-hugeicons-delete-02"
                sm
                ruby
                faded
                :disabled="isMoving"
                @click="deleteDialogRef?.open()"
              />
            </div>
          </div>
          <header class="grid gap-1 border-b border-n-weak pb-3">
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ opened.subject || $t('EMAIL_FOLDERS.NO_SUBJECT') }}
            </h2>
            <p class="m-0 text-xs text-n-slate-11">
              {{ senderLabel(opened.from?.[0]) }}
              <span v-if="opened.from?.[0]?.email"
                >&lt;{{ opened.from[0].email }}&gt;</span
              >
              · {{ formatDate(opened.date) }}
            </p>
          </header>
          <div
            v-if="hasBlockedImages"
            class="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11"
          >
            <span
              class="i-hugeicons-image-not-found-01 size-4"
              aria-hidden="true"
            />
            {{ $t('EMAIL_HEADER.REMOTE_IMAGES_BLOCKED') }}
            <button
              type="button"
              class="font-semibold text-n-blue-11 hover:underline"
              @click="showRemoteImages = true"
            >
              {{ $t('EMAIL_HEADER.SHOW_REMOTE_IMAGES') }}
            </button>
          </div>
          <div class="email-paper rounded-lg border border-n-weak p-3">
            <Letter
              :key="`${openedUid}-${showRemoteImages}`"
              class-name="email-html !max-w-none letter-render"
              :html="opened.html"
              :text="opened.text"
              :allowed-css-properties="allowedCssProperties"
              :rewrite-external-resources="resourceRewriter"
            />
          </div>
          <ul
            v-if="opened.attachments?.length"
            class="m-0 flex list-none flex-wrap gap-2 p-0"
          >
            <li
              v-for="attachment in opened.attachments"
              :key="attachment.index"
            >
              <button
                type="button"
                class="inline-flex max-w-72 items-center gap-2 rounded-lg border px-3 py-1.5 text-xs"
                :class="
                  attachment.risky
                    ? 'border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11'
                    : 'border-n-weak text-n-slate-12 hover:bg-n-alpha-1'
                "
                @click="requestAttachment(attachment)"
              >
                <span
                  :class="
                    attachment.risky
                      ? 'i-hugeicons-alert-02'
                      : 'i-hugeicons-download-04'
                  "
                  class="size-4 shrink-0"
                  aria-hidden="true"
                />
                <span class="truncate">{{ attachment.filename }}</span>
                <span class="shrink-0 text-n-slate-10">{{
                  formatSize(attachment.size)
                }}</span>
              </button>
            </li>
          </ul>
        </article>
      </div>
    </div>

    <Dialog
      v-if="canDeletePermanently"
      ref="deleteDialogRef"
      type="alert"
      :title="$t('EMAIL_FOLDERS.DELETE_DIALOG.TITLE')"
      :description="$t('EMAIL_FOLDERS.DELETE_DIALOG.DESCRIPTION')"
      :confirm-button-label="$t('EMAIL_FOLDERS.DELETE_DIALOG.CONFIRM')"
      :is-loading="isMoving"
      @confirm="runOperation('delete_permanently')"
    />
    <Dialog
      ref="riskyDialogRef"
      type="alert"
      :title="$t('EMAIL_FOLDERS.RISKY_DIALOG.TITLE')"
      :description="
        $t('EMAIL_FOLDERS.RISKY_DIALOG.DESCRIPTION', {
          name: pendingRiskyAttachment?.filename || '',
        })
      "
      :confirm-button-label="$t('EMAIL_FOLDERS.RISKY_DIALOG.CONFIRM')"
      @confirm="confirmRiskyDownload"
    />
  </section>
</template>
